/**
 * Landing page for the SSO redirect: /sso/complete?code=...
 *
 * The backend never puts tokens in the URL, so it hands over a one-time code
 * that we immediately trade for the real login payload. The code dies after a
 * single use and expires within a minute, so this page has one job and should
 * do it without waiting for anything the user has to click.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { aig_portal_session, sso_exchange_code } from '../../services';
import ProfileCompletionModal from '../../Components/ProfileCompletionModal';
import { isOrgAdminOnly } from '../../helper';
import '../Auth.scss';
import {
  completeLoginSideEffects,
  consumeRedirectTarget,
  isProfileIncomplete,
  persistLoginSession,
} from './loginSession';

/**
 * Give the audit portal the same identity a password portal login would have.
 *
 * The platform session says who the user is; ModuleAccess says what they are in
 * the portal. `via: 'sso'` marks these keys as authoritative even though a
 * dashboard session also exists -- a portal token left over from testing has no
 * such marker, so existing behaviour is untouched.
 *
 * @returns {Promise<string>} an error message to show, or '' on success
 */
async function establishAuditPortalSession(loginData) {
  sessionStorage.removeItem('audit_portal_sso');

  try {
    const response = await aig_portal_session();
    const portal = response?.statusCode?.response_data?.data;
    const apiError =
      response?.errors?.message || response?.statusCode?.error?.message;

    if (!portal?.role) {
      // Signed in successfully, but not a portal user. Say so plainly rather
      // than dropping them into a portal with no role.
      sessionStorage.removeItem('redirect_after_login');
      return apiError || 'You do not have access to the Audit Portal.';
    }

    localStorage.setItem('audit_portal_token', loginData.access_token);
    localStorage.setItem('audit_portal_refresh', loginData.refresh_token);
    localStorage.setItem('audit_portal_user', JSON.stringify({
      email: portal.email,
      module: portal.module || 'audit',
      role: portal.role,
      first_name: portal.first_name,
      last_name: portal.last_name,
      organization_id: portal.organization_id,
      via: 'sso',
    }));
    return '';
  } catch (error) {
    console.error('Audit portal session lookup failed:', error);
    sessionStorage.removeItem('redirect_after_login');
    return 'We could not load your Audit Portal access. Please try again.';
  }
}

function SsoComplete() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [errorMessage, setErrorMessage] = useState('');
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [profileData, setProfileData] = useState(null);

  // React 18+ StrictMode mounts effects twice in development. The code is
  // single-use, so a second exchange would fail and show a spurious error.
  const hasExchanged = useRef(false);

  useEffect(() => {
    if (hasExchanged.current) return;
    hasExchanged.current = true;

    const code = searchParams.get('code');
    if (!code) {
      setErrorMessage('This sign-in link is incomplete. Please sign in again.');
      return;
    }

    const exchange = async () => {
      try {
        const response = await sso_exchange_code({ code });
        const data = response?.statusCode?.response_data?.data;
        const apiError =
          response?.errors?.message || response?.statusCode?.error?.message;

        if (!data?.access_token) {
          setErrorMessage(
            apiError || 'This sign-in link has expired. Please sign in again.'
          );
          return;
        }

        persistLoginSession(data, data?.user?.emailId);

        // Only when the sign-in began at the audit portal login screen. Its
        // role comes from ModuleAccess, not from the platform session, so it
        // has to be fetched before the portal renders anything.
        if (sessionStorage.getItem('audit_portal_sso') === '1') {
          const portalError = await establishAuditPortalSession(data);
          if (portalError) {
            setErrorMessage(portalError);
            return;
          }
        }

        const profile = await completeLoginSideEffects(data.organization_id);
        // Only the org admin owns the org-level profile fields; the backend
        // rejects anyone else. Other members of the same org sign straight
        // through (and it won't reopen once the admin has completed it).
        if (profile && isOrgAdminOnly() && isProfileIncomplete(profile)) {
          setProfileData(profile);
          setShowProfileModal(true);
          return;
        }

        navigate(consumeRedirectTarget(), { replace: true });
      } catch (error) {
        console.error('SSO exchange failed:', error);
        setErrorMessage('We could not complete your sign-in. Please try again.');
      }
    };

    exchange();
  }, [searchParams, navigate]);

  const handleProfileComplete = () => {
    setShowProfileModal(false);
    navigate(consumeRedirectTarget(), { replace: true });
  };

  if (errorMessage) {
    return (
      <div className="sso_status_wrapper">
        <div className="sso_status_card">
          <h2 className="sso_status_title">Sign-in didn’t complete</h2>
          <p className="sso_status_text">{errorMessage}</p>
          <button
            type="button"
            className="sso_status_btn"
            onClick={() => navigate('/', { replace: true })}
          >
            Back to sign in
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="sso_status_wrapper">
        <div className="sso_status_card">
          <div className="sso_spinner" role="status" aria-label="Signing you in" />
          <h2 className="sso_status_title">Signing you in…</h2>
          <p className="sso_status_text">
            Your organisation has confirmed your identity. One moment.
          </p>
        </div>
      </div>

      <ProfileCompletionModal
        show={showProfileModal}
        onHide={() => { }}
        onSuccess={handleProfileComplete}
        existingProfile={profileData}
      />
    </>
  );
}

export default SsoComplete;
